<template>
  <div>
    <div v-if="!loading">
      <!-- Year selector -->
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <button
          v-for="(year, index) in availableYears"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedYear === year
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectYear(year)"
        >
          {{ year }}
        </button>
      </div>

      <!-- Month selector -->
      <div class="flex flex-wrap items-center gap-2 mb-6">
        <button
          v-for="(month, index) in availableMonths"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedMonth === month.number
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectMonth(month)"
        >
          {{ month.label }}
        </button>
      </div>

      <!-- Empty state when no month selected -->
      <div v-if="!report && selectedMonth === null" class="card text-center py-10">
        <p class="text-content-300">Selecciona un mes para ver el reporte.</p>
      </div>

      <div v-if="report">
        <!-- General stats grid -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Rendimiento Operativo — General</h2>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Total de órdenes</p>
            <p class="text-2xl font-bold text-gray-800">{{ report.total_orders }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Órdenes entregadas</p>
            <p class="text-2xl font-bold text-gray-800">{{ report.total_delivered }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Órdenes a tiempo</p>
            <p class="text-2xl font-bold text-success-500">{{ report.on_time_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Órdenes tardías</p>
            <p class="text-2xl font-bold text-error-500">{{ report.late_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-success-500">
            <p class="text-sm text-gray-500 mb-1">Tasa de puntualidad</p>
            <p
              class="text-2xl font-bold"
              :class="parseFloat(report.on_time_rate) >= 90 ? 'text-success-500' : parseFloat(report.on_time_rate) >= 75 ? 'text-warning-500' : 'text-error-500'"
            >
              {{ parseFloat(report.on_time_rate).toFixed(1) }}%
            </p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Retraso promedio</p>
            <p class="text-2xl font-bold" :class="parseFloat(report.avg_delay) > 1 ? 'text-warning-500' : 'text-gray-800'">
              {{ report.avg_delay }} <span class="text-base font-normal text-gray-500">días</span>
            </p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Órdenes fallidas</p>
            <p class="text-2xl font-bold text-error-500">{{ report.failed_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Costos de envío</p>
            <p class="text-2xl font-bold text-gray-800">{{ integerToCurrency(report.total_shipping) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Entregas a domicilio</p>
            <p class="text-2xl font-bold text-gray-800">{{ report.delivery_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Recolecciones</p>
            <p class="text-2xl font-bold text-gray-800">{{ report.pickup_count }}</p>
          </div>
        </div>

        <!-- Fail reasons -->
        <div class="bg-white rounded-lg shadow p-4 mb-8">
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Razones de fallo</h3>
          <div v-if="report.fail_reasons && report.fail_reasons.length > 0">
            <div
              v-for="reason in report.fail_reasons"
              :key="reason.reason"
              class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <span class="text-sm text-gray-600">{{ reason.reason }}</span>
              <span class="ml-4 px-2 py-0.5 bg-red-100 text-error-500 rounded-full text-xs font-semibold">{{ reason.count }}</span>
            </div>
          </div>
          <p v-else class="text-sm text-content-300">Sin fallos registrados</p>
        </div>

        <!-- Franchises section -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Franquicias</h2>

        <div
          v-if="!report.franchises || report.franchises.length === 0"
          class="card text-center py-6"
        >
          <p class="text-content-300">Sin datos para este período</p>
        </div>

        <div class="flex flex-col gap-4">
          <div
            v-for="franchise in report.franchises"
            :key="franchise.franchise_id"
            class="bg-white rounded-lg shadow overflow-hidden"
          >
            <!-- Franchise header -->
            <div
              class="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
              @click="toggleFranchise(franchise.franchise_id)"
            >
              <div class="flex items-center gap-3">
                <div class="w-2 h-8 rounded-full bg-primary-500"></div>
                <div>
                  <h3 class="font-semibold text-gray-800">{{ franchise.name }}</h3>
                  <p class="text-xs text-gray-500">{{ franchise.branches ? franchise.branches.length : 0 }} sucursal(es)</p>
                </div>
              </div>
              <div class="flex items-center gap-4">
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Órdenes</p>
                  <p class="font-semibold text-gray-700">{{ franchise.total_orders }}</p>
                </div>
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">A tiempo</p>
                  <p class="font-semibold text-success-500">{{ franchise.on_time_count }}</p>
                </div>
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Puntualidad</p>
                  <p
                    class="font-semibold"
                    :class="parseFloat(franchise.on_time_rate) >= 90 ? 'text-success-500' : parseFloat(franchise.on_time_rate) >= 75 ? 'text-warning-500' : 'text-error-500'"
                  >
                    {{ parseFloat(franchise.on_time_rate).toFixed(1) }}%
                  </p>
                </div>
                <div class="text-right hidden md:block">
                  <p class="text-xs text-gray-500">Fallidas</p>
                  <p class="font-semibold text-error-500">{{ franchise.failed_count }}</p>
                </div>
                <svg
                  :class="['w-5 h-5 text-gray-400 transition-transform', isFranchiseExpanded(franchise.franchise_id) ? 'rotate-180' : '']"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <!-- Expanded branch table -->
            <div v-if="isFranchiseExpanded(franchise.franchise_id)" class="border-t border-gray-100">
              <!-- Stats row for mobile -->
              <div class="flex flex-wrap gap-4 px-5 py-3 bg-gray-50 sm:hidden text-sm">
                <div><span class="text-gray-500">Órdenes:</span> <span class="font-semibold">{{ franchise.total_orders }}</span></div>
                <div><span class="text-gray-500">A tiempo:</span> <span class="font-semibold text-success-500">{{ franchise.on_time_count }}</span></div>
                <div><span class="text-gray-500">Puntualidad:</span> <span class="font-semibold">{{ parseFloat(franchise.on_time_rate).toFixed(1) }}%</span></div>
                <div><span class="text-gray-500">Fallidas:</span> <span class="font-semibold text-error-500">{{ franchise.failed_count }}</span></div>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead class="bg-primary-100">
                    <tr>
                      <th class="px-5 py-2 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Sucursal</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Órdenes</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">A tiempo</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Tardías</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Puntualidad</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Fallidas</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Domicilio</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Recolección</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Envío</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(branch, bIndex) in franchise.branches"
                      :key="branch.branch_id"
                      :class="bIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    >
                      <td class="px-5 py-2 font-medium text-gray-800">{{ branch.branch_name }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ branch.total_orders }}</td>
                      <td class="px-4 py-2 text-right text-success-500 font-medium">{{ branch.on_time_count }}</td>
                      <td class="px-4 py-2 text-right text-error-500 font-medium">{{ branch.late_count }}</td>
                      <td
                        class="px-4 py-2 text-right font-semibold"
                        :class="parseFloat(branch.on_time_rate) >= 90 ? 'text-success-500' : parseFloat(branch.on_time_rate) >= 75 ? 'text-warning-500' : 'text-error-500'"
                      >
                        {{ parseFloat(branch.on_time_rate).toFixed(1) }}%
                      </td>
                      <td class="px-4 py-2 text-right text-error-500">{{ branch.failed_count }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ branch.delivery_count }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ branch.pickup_count }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ integerToCurrency(branch.total_shipping) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Operational",

  data() {
    return {
      report: null,
      loading: false,
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      expandedFranchises: [],
      months: [
        { number: 1, label: "Enero" },
        { number: 2, label: "Febrero" },
        { number: 3, label: "Marzo" },
        { number: 4, label: "Abril" },
        { number: 5, label: "Mayo" },
        { number: 6, label: "Junio" },
        { number: 7, label: "Julio" },
        { number: 8, label: "Agosto" },
        { number: 9, label: "Septiembre" },
        { number: 10, label: "Octubre" },
        { number: 11, label: "Noviembre" },
        { number: 12, label: "Diciembre" },
      ],
    };
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const startingYear = 2020;
      const years = [];

      for (let year = startingYear; year <= currentYear; year++) {
        years.push(year);
      }

      return years;
    },

    availableMonths() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (this.selectedYear === currentYear) {
        return this.months.filter((month) => month.number <= currentMonth);
      }

      return this.months;
    },
  },

  created() {
    this.loadReport();
  },

  methods: {
    integerToCurrency,

    toggleFranchise(id) {
      const index = this.expandedFranchises.indexOf(id);
      if (index === -1) {
        this.expandedFranchises.push(id);
      } else {
        this.expandedFranchises.splice(index, 1);
      }
    },

    isFranchiseExpanded(id) {
      return this.expandedFranchises.includes(id);
    },

    async loadReport() {
      this.loading = true;

      try {
        this.report = await api.reports.getOperationalReport([
          { name: "year", value: this.selectedYear },
          {
            name: "month",
            value: this.selectedMonth,
          },
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    selectYear(year) {
      if (year === this.selectedYear) {
        return;
      }

      this.selectedYear = year;
      this.selectedMonth = null;
      this.report = null;
    },

    selectMonth(month) {
      if (this.selectedMonth === month) {
        return;
      }

      this.selectedMonth = month.number;

      this.loadReport();
    },
  },
};
</script>
